/**
 * Focus and selection return (PLAN D6). On open the palette remembers the
 * focused element and, for an input / textarea, its selection (start, end,
 * direction); for a contenteditable, the DOM range. Closing WITHOUT
 * navigating gives both back; Monaco and xterm get focus back through their
 * helper textarea, Lexical restores its own selection on focus. If the opener
 * left the DOM, the active page's main region gets focus instead.
 */

function isTextControl(el) {
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  if (tag === 'textarea') return true;
  if (tag !== 'input') return false;
  return /^(text|search|url|tel|password|email|)$/i.test(el.type || '');
}

export function captureOpener(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc) return null;
  const el = doc.activeElement;
  if (!el || el === doc.body || el === doc.documentElement) return { el: null };
  const info = { el };
  if (isTextControl(el)) {
    try {
      info.selection = { start: el.selectionStart, end: el.selectionEnd, direction: el.selectionDirection || 'none' };
    } catch {
      /* some input types have no selection API */
    }
  } else if (el.isContentEditable) {
    const sel = doc.getSelection?.();
    if (sel && sel.rangeCount && el.contains(sel.anchorNode)) info.range = sel.getRangeAt(0).cloneRange();
  }
  return info;
}

/** The active page's main region: `[data-page-root]`, else the main layout. */
export function pageRoot(doc = document) {
  return doc.querySelector('[data-page-root]') || doc.querySelector('.app-main-layout') || null;
}

export function restoreOpener(info, doc = document) {
  const el = info?.el;
  if (el && el.isConnected) {
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus?.();
    }
    if (info.selection && isTextControl(el) && info.selection.start != null) {
      try {
        el.setSelectionRange(info.selection.start, info.selection.end, info.selection.direction);
      } catch {
        /* ignore */
      }
    } else if (info.range && info.range.startContainer?.isConnected) {
      const sel = doc.getSelection?.();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(info.range);
      }
    }
    return el;
  }
  const root = pageRoot(doc);
  if (root) {
    if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
    root.focus({ preventScroll: true });
    return root;
  }
  return null;
}
